import type { InitiateSurveyV3Mutation } from '@/survey-graphql';

import { WidgetUtSegmentRecorder } from './project-usability-widget-segment-recorder';

export const UT_SEGMENT_MIME_TYPE = 'video/webm';
const SESSION_MAX_AGE_MS = 2 * 60 * 60 * 1000;

type WidgetUtFirstQuestion =
  InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];

/**
 * A widget UT records the host website itself, so every real page navigation
 * destroys the MediaRecorder. This state survives navigations in localStorage
 * so the next page load can offer to resume: the conversation continues on the
 * same insight and the recording continues as a new segment.
 */
export interface WidgetUtSessionState {
  version: 1;
  projectId: string;
  uniqueId: string;
  sessionId: string;
  insightId: string | null;
  firstQuestion: WidgetUtFirstQuestion;
  /** Index the NEXT recording segment will be uploaded under */
  nextSegmentIndex: number;
  /** Total recorded duration of all sealed segments, in ms */
  elapsedMsBefore: number;
  startedAt: number;
}

// Session state lives in sessionStorage (per-tab), not localStorage: resume must
// only be offered to the same visitor who started the test. localStorage would
// let a second visitor on a shared/kiosk device resume under the first visitor's
// insightId/sessionId within the TTL. sessionStorage still survives the same-tab
// host-page navigations this feature resumes across.
const storageKey = (projectId: string) => `ts_ut_widget_session_${projectId}`;
const dismissedKey = (projectId: string) =>
  `ts_ut_widget_dismissed_${projectId}`;

/**
 * "Never show again": the visitor opted out of the in-widget usability test for
 * this project. Persisted separately from the session so it survives a cleared
 * session and permanently suppresses the launcher.
 */
export function isWidgetUtDismissed(projectId: string): boolean {
  try {
    return window.localStorage.getItem(dismissedKey(projectId)) === '1';
  } catch {
    return false;
  }
}

export function setWidgetUtDismissed(projectId: string) {
  try {
    window.localStorage.setItem(dismissedKey(projectId), '1');
  } catch {
    // Safari private mode throws on writes; the opt-out just won't persist.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFiniteNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Validates every field, not just the version tag: a corrupt record with a
 * valid version but a missing/non-numeric `startedAt` would otherwise make the
 * TTL check `NaN > MAX` (false) and let garbage flow into the resume path.
 */
function isValidWidgetUtSession(value: unknown): value is WidgetUtSessionState {
  return (
    isRecord(value) &&
    value.version === 1 &&
    typeof value.projectId === 'string' &&
    typeof value.uniqueId === 'string' &&
    typeof value.sessionId === 'string' &&
    (typeof value.insightId === 'string' || value.insightId === null) &&
    isRecord(value.firstQuestion) &&
    isFiniteNumber(value.nextSegmentIndex) &&
    isFiniteNumber(value.elapsedMsBefore) &&
    isFiniteNumber(value.startedAt)
  );
}

export function loadWidgetUtSession(
  projectId: string
): WidgetUtSessionState | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidWidgetUtSession(parsed)) {
      window.sessionStorage.removeItem(storageKey(projectId));
      return null;
    }
    if (Date.now() - parsed.startedAt > SESSION_MAX_AGE_MS) {
      window.sessionStorage.removeItem(storageKey(projectId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveWidgetUtSession(state: WidgetUtSessionState) {
  try {
    window.sessionStorage.setItem(
      storageKey(state.projectId),
      JSON.stringify(state)
    );
  } catch {
    // Safari private mode throws on writes; the session just won't resume.
  }
}

export function clearWidgetUtSession(projectId: string) {
  try {
    window.sessionStorage.removeItem(storageKey(projectId));
  } catch {
    // best-effort
  }
}

/**
 * The legacy widget's recorder: the generic segment recorder bound to the
 * legacy session record and its sessionStorage persistence.
 */
export class WidgetUtRecorder extends WidgetUtSegmentRecorder<WidgetUtSessionState> {
  constructor(state: WidgetUtSessionState) {
    super(state, saveWidgetUtSession);
  }
}

let activeRecorder: WidgetUtRecorder | null = null;

export function setActiveWidgetUtRecorder(recorder: WidgetUtRecorder | null) {
  activeRecorder = recorder;
}

/**
 * Recording clock for widget UT sessions. Recording-event mutations use this
 * (via useUsabilityRecordingDuration) so chat markers land on the merged
 * recording's timeline instead of the theysaid-page recorder's.
 */
export function getWidgetUtRecordingTimeSec(): number | null {
  if (!activeRecorder || !activeRecorder.isRecording()) return null;
  return activeRecorder.getCurrentRecordingTimeSec();
}
